import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import ActivityEditDialog from '@totem/components/ticketing/details/activities/activityEditDialog';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { StatusHistory } from '@totem/components/ticketing/optimizeTypes';
import {
  Activity,
  ActivityConfiguration,
  OptimizeDeferralRequest,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
type Props = {
  ticketId: string;
  children?: ReactNode;
};
const TicketContainer = ({ ticketId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ticketData, setTicketData] = useState<TicketQueryResult>(null);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [action, setAction] = useState<string>('');
  const [actionExtras, setActionExtras] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [eligibleActivities, setEligibleActivities] = useState<
    ActivityConfiguration[]
  >([]);
  useEffect(() => {
    setRefresh(true);
  }, [ticketId]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);

      fetch(`${TICKET_ENDPOINT}/${ticketId}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: TicketQueryResult) => {
          setTicketData(result);
        })
        .then(() => setIsLoading(false));
    }
  }, [refresh, getApiToken]);

  useEffect(() => {
    if (
      isNotNull(ticketData) &&
      isNotNull(ticketData.ticket) &&
      ticketData.ticket.id !== ''
    ) {
      fetch(
        `${TICKET_ENDPOINT}/${ticketData.ticket.id}/eligibleActivities/${ticketData.ticket.organizationId}`,
        {
          method: 'GET',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      )
        .then((res) => res.json())
        .then((result: ActivityConfiguration[]) => {
          setEligibleActivities(result);
        });
    }
  }, [ticketData, getApiToken]);
  const handleTicketUpdate = (updatedData: TicketQueryResult) => {
    setTicketData(updatedData);
  };
  const handleAction = (newAction: string, extras: string) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);

        setAction('');
        break;
      case 'close-activity-dialog':
        setAction('');
        break;
      default:
        setActionExtras(extras);

        setAction(newAction);
    }
  };
  const sendActivity = useCallback(
    async (paramTicketId: string, request: Activity) => {
      if (isSending) {
        return;
      }
      fetch(`${TICKET_ENDPOINT}/${paramTicketId}/activity`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((res) => res.json())
        .then((result) => {
          handleTicketUpdate(result);

          handleAction('refresh', '');
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );
  const handleCreateActivity = (request: Activity) => {
    sendActivity(ticketId, request);
  };

  const sendOptimizeDeferral = useCallback(
    async (paramTicketId: string, payload: OptimizeDeferralRequest) => {
      if (!isSending) {
        setIsSending(true);

        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/optimize/defer`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            handleAction('refresh', '');
          }
        });
      }
    },

    [isSending, getApiToken, handleAction],
  );

  const handleDeferral = (deferral: OptimizeDeferralRequest) => {
    sendOptimizeDeferral(ticketData.ticket.id, deferral);
  };

  const sendOptimizeStatus = useCallback(
    async (paramTicketId: string, payload: StatusHistory) => {
      if (!isSending) {
        setIsSending(true);

        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/optimize/status`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            handleAction('refresh', '');
          }
        });
      }
    },

    [isSending, getApiToken, handleAction],
  );

  const handleOptimizeStatus = (status: StatusHistory) => {
    sendOptimizeStatus(ticketData.ticket.id, status);
  };

  return (
    <TicketContext.Provider
      value={{
        ticket: ticketData,
        activityConfiguration: isNotNull(eligibleActivities)
          ? eligibleActivities
          : [],
        loading: isLoading,
        action,
        actionExtras,
        onAction: handleAction,
        onUpdateTicket: handleTicketUpdate,
        onCreateActivity: handleCreateActivity,
        onOptimizeDeferral: handleDeferral,
        onOptimizeStatusChange: handleOptimizeStatus,
      }}
    >
      {children}
      {action === 'show-activity-create' && <ActivityEditDialog />}
    </TicketContext.Provider>
  );
};
export default TicketContainer;
